import {useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {AnimatePresence, motion} from "motion/react";
import {X} from "lucide-react";
import {cn} from "@/lib/utils";

interface SortableItemProps {
  id: string;
  isEditMode: boolean;
  isDragging: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function SortableItem({
  id,
  isEditMode,
  isDragging,
  onDelete,
  isDeleting,
  children,
  className,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({id, disabled: !isEditMode});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "relative h-full min-w-0 touch-none select-none",
        isEditMode && !isSortableDragging && "cursor-grab",
        isSortableDragging && "z-50 cursor-grabbing",
        className
      )}
    >
      {/* Delete button - iOS-style red circle with X */}
      <AnimatePresence>
        {isEditMode && onDelete && (
          <motion.button
            type="button"
            initial={{scale: 0, opacity: 0}}
            animate={{scale: 1, opacity: 1}}
            exit={{scale: 0, opacity: 0}}
            transition={{type: "spring", stiffness: 500, damping: 30}}
            whileHover={{scale: 1.1}}
            whileTap={{scale: 0.95}}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete();
            }}
            disabled={isDeleting}
            className={cn(
              "absolute -top-2 -right-2 z-30",
              "flex h-6 w-6 items-center justify-center rounded-full",
              "bg-destructive text-destructive-foreground",
              "ring-background shadow-lg ring-2",
              isDeleting && "pointer-events-none opacity-50"
            )}
            title="Remove from favorites"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2.5} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Card content wrapper with animations */}
      <div
        className={cn(
          "h-full w-full overflow-hidden transition-all duration-200",
          // Shake animation when in edit mode but not actively dragging
          isEditMode && !isDragging && !isSortableDragging && "animate-shake-edit",
          // Scale and shadow when this item is being dragged
          isSortableDragging && "scale-105 rotate-1 opacity-95 shadow-2xl shadow-black/25"
        )}
      >
        {children}
      </div>
    </div>
  );
}
